import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import clinicAPI from "../../../services/clinicApi";

import { HubFormCard, HubSheet, HubSheetActions, DesktopDialog } from "../AdminPortal/HubPwa";

import { useIsMobile } from "../../../hooks/useMediaQuery";

import aa from "../AdminPortal/adminApp.module.css";

import { PATIENTS_BASE, isPortalLoginEmail } from "./patientConstants";

import L from "../../../constants/baysportLabels";

import { ageFromDob } from "../../../utils/clientAppUtils";

import { showPortalInviteFeedback } from "../../../utils/portalInviteFeedback";
import { getApiErrorMessage } from "../../../utils/apiError";
import { trackActivationComplete } from "../../../analytics/metrikaGoals";

import { Button, Field, Input, PhoneInput, Select, CheckLabel, useToast } from "../../ui";



const EMPTY_CHILD = { fullName: "", date_of_birth: "", email: "" };



function ClientInviteOptions({

  sendPortalInvite,

  onSendPortalInviteChange,

  sendInviteEmail,

  onSendInviteEmailChange,

  hasEmail,

  solo = false,

  childrenWithEmail = 0,

}) {

  const who = solo ? "клиенту" : "родителю";



  return (

    <div className={aa.clientAddInviteBlock}>

      <CheckLabel strong>

        <input

          type="checkbox"

          checked={sendPortalInvite}

          onChange={(e) => onSendPortalInviteChange(e.target.checked)}

        />

        <span>Отправить приглашение в личный кабинет</span>

      </CheckLabel>



      {sendPortalInvite ? (

        <>

          <p className={aa.clientAddSheetNote}>

            {who.charAt(0).toUpperCase() + who.slice(1)} получит ссылку на email для активации

            кабинета и установки пароля (действует 7 суток).

            {!solo && childrenWithEmail > 0 ? (
              <>
                {" "}
                Детям с указанным email уйдёт отдельное приглашение — у каждого будет
                свой вход в кабинет.
              </>
            ) : null}

          </p>



          {hasEmail ? (

            <CheckLabel className={aa.clientAddInviteCheck}>

              <input

                type="checkbox"

                checked={sendInviteEmail}

                onChange={(e) => onSendInviteEmailChange(e.target.checked)}

              />

              <span>Отправить приглашение на email</span>

            </CheckLabel>

          ) : (

            <p className={aa.clientAddSheetNoteWarn}>

              Укажите email — иначе после сохранения скопируйте ссылку и передайте {who} вручную.

            </p>

          )}

        </>

      ) : (

        <p className={aa.clientAddSheetNote}>

          Клиент будет создан без отправки ссылки. Приглашение можно отправить позже из

          карточки клиента.

        </p>

      )}

    </div>

  );

}



function ChildDobField({ label, value, onChange, required }) {

  const age = ageFromDob(value);

  return (

    <Field

      label={label}

      hint={age != null ? `Возраст: ${age} лет` : undefined}

    >

      <Input

        type="date"

        required={required}

        value={value}

        onChange={onChange}

        max={new Date().toISOString().slice(0, 10)}

      />

    </Field>

  );

}



export default function ClientAddSheet({ open, onClose, onCreated }) {
  const tenantId = useSelector((state) => state.auth.tenant?.id);

  const isMobile = useIsMobile(768);

  const navigate = useNavigate();

  const toast = useToast();

  const [groups, setGroups] = useState([]);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [isFamily, setIsFamily] = useState(false);

  const [sendPortalInvite, setSendPortalInvite] = useState(true);

  const [sendInviteEmail, setSendInviteEmail] = useState(true);

  const [children, setChildren] = useState([{ ...EMPTY_CHILD }]);

  const [form, setForm] = useState({

    fullName: "",

    phone: "",

    email: "",

    date_of_birth: "",

    parentName: "",

    parentPhone: "",

    parentEmail: "",

    group_id: "",

  });



  useEffect(() => {

    if (!open) return;

    setFormError("");

    setForm({

      fullName: "",

      phone: "",

      email: "",

      date_of_birth: "",

      parentName: "",

      parentPhone: "",

      parentEmail: "",

      group_id: "",

    });

    setChildren([{ ...EMPTY_CHILD }]);

    setIsFamily(false);

    setSendPortalInvite(true);

    setSendInviteEmail(true);

    clinicAPI

      .getSportGroupsSummary()

      .then((r) => setGroups(r.groups || []))

      .catch(() =>

        clinicAPI.getServicesCatalog().then((c) => setGroups(c.specializations || [])),

      );

  }, [open]);



  const contactEmail = isFamily ? form.parentEmail : form.email;

  const hasEmail = isPortalLoginEmail(contactEmail);

  const invitePayload = () => ({
    send_invite: sendPortalInvite,
    send_invite_email: sendPortalInvite && hasEmail && sendInviteEmail,
  });

  const assertCreateResponse = (r) => {
    if (r?.result !== undefined && r.result !== 0) {
      throw new Error(r.error || r.message || "Не удалось создать клиента");
    }
    return r;
  };

  const reportCreateError = (err) => {
    const message = getApiErrorMessage(err, "Ошибка создания");
    setFormError(message);
    toast.error(message);
  };



  const notifyInviteResult = async (r, fallbackMessage) => {

    if (!sendPortalInvite) {

      toast.success(fallbackMessage);

      return;

    }

    await showPortalInviteFeedback(

      toast,

      {

        invite_url: r.invite_url,

        invite_email_sent: r.invite_email_sent,

        invite_sms_sent: r.invite_sms_sent,

        message: r.message,

      },

      fallbackMessage,

    );

    const childInvites = Array.isArray(r.child_invites) ? r.child_invites : [];

    for (const ci of childInvites) {

      if (!ci.invite_url && !ci.invite_email_sent) continue;

      await showPortalInviteFeedback(

        toast,

        {

          invite_url: ci.invite_url,

          invite_email_sent: ci.invite_email_sent,

          message: ci.invite_email_sent

            ? `Приглашение для ${ci.full_name} отправлено на email`

            : `Ссылка для ${ci.full_name} скопирована`,

        },

        `Приглашение для ${ci.full_name}`,

      );

    }

  };



  const patchChild = (idx, patch) => {

    setChildren((list) =>

      list.map((c, i) => (i === idx ? { ...c, ...patch } : c)),

    );

  };



  const submitSolo = async () => {

    if (!form.fullName.trim()) {
      toast.error("Укажите ФИО клиента");
      return;
    }

    if (sendPortalInvite && sendInviteEmail && !hasEmail) {
      toast.error("Для email-приглашения укажите адрес почты");
      return;
    }

    const r = assertCreateResponse(
      await clinicAPI.createPatient({

      fullName: form.fullName.trim(),

      phone: form.phone.trim() || undefined,

      email: form.email.trim() || undefined,

      date_of_birth: form.date_of_birth || undefined,

      password: "",

      group_id: form.group_id || undefined,

      ...invitePayload(),

    }),
    );

    await notifyInviteResult(r, "Клиент добавлен");

    trackActivationComplete("client_added", tenantId);

    onClose?.();

    const patientId = r.patient?.id;

    if (patientId) {

      onCreated?.(patientId);

      navigate(`${PATIENTS_BASE}/${patientId}`);

    } else {

      onCreated?.();

    }

  };



  const submitFamily = async () => {

    if (!form.parentName.trim()) {
      toast.error("Укажите ФИО родителя");
      return;
    }

    if (sendPortalInvite && sendInviteEmail && !hasEmail) {
      toast.error("Для email-приглашения укажите email родителя");
      return;
    }

    const childRows = children

      .map((c) => ({

        fullName: c.fullName.trim(),

        date_of_birth: c.date_of_birth || null,

        email: c.email?.trim() || undefined,

      }))

      .filter((c) => c.fullName);

    if (!childRows.length) {

      toast.error("Добавьте хотя бы одного ребёнка");

      return;

    }

    if (childRows.some((c) => !c.date_of_birth)) {

      toast.error("Укажите дату рождения каждого ребёнка");

      return;

    }



    const r = assertCreateResponse(
      await clinicAPI.createClientFamily({

      parent: {

        fullName: form.parentName.trim(),

        phone: form.parentPhone.trim() || undefined,

        email: form.parentEmail.trim() || undefined,

      },

      children: childRows,

      group_id: form.group_id || undefined,

      ...invitePayload(),

    }),
    );

    await notifyInviteResult(r, "Семья создана");

    trackActivationComplete("client_family_added", tenantId);

    onClose?.();

    const athleteId = r.athletes?.[0]?.id;

    if (athleteId) {

      onCreated?.(athleteId);

      navigate(`${PATIENTS_BASE}/${athleteId}`);

    } else {

      onCreated?.();

    }

  };



  const submit = async (e) => {

    e.preventDefault();

    setFormError("");
    setSaving(true);

    try {

      if (isFamily) await submitFamily();

      else await submitSolo();

    } catch (err) {

      reportCreateError(err);

    } finally {

      setSaving(false);

    }

  };



  const canSave = isFamily
    ? form.parentName.trim() &&
      children.some((c) => c.fullName.trim() && c.date_of_birth)
    : form.fullName.trim();



  const inviteOptions = (

    <ClientInviteOptions

      solo={!isFamily}
      hasEmail={hasEmail}
      childrenWithEmail={
        isFamily
          ? children.filter((c) => isPortalLoginEmail(c.email)).length
          : 0
      }
      sendPortalInvite={sendPortalInvite}
      onSendPortalInviteChange={setSendPortalInvite}
      sendInviteEmail={sendInviteEmail}
      onSendInviteEmailChange={setSendInviteEmail}
    />

  );



  const sheetFooter = (

        <HubSheetActions

          primary={

            saving

              ? "Сохранение…"

              : sendPortalInvite

                ? "Сохранить и отправить"

                : "Сохранить"

          }

          primaryForm="client-add-form"

          primaryDisabled={saving || !canSave}

          onSecondary={onClose}

        />

      );

  const sheetSubtitle =

        isFamily

          ? "Родитель и один или несколько детей"

          : "Один спортсмен — телефон и имя";

  const Sheet = isMobile ? HubSheet : DesktopDialog;

  const sheetExtraProps = isMobile

    ? { sheetClassName: aa.hubSheetCenterWide }

    : { wide: true };



  return (

    <Sheet

      open={open}

      onClose={onClose}

      title="Новый клиент"

      subtitle={sheetSubtitle}

      titleId="client-add-sheet-title"

      bodyClassName={aa.clientAddSheetBody}

      footer={sheetFooter}

      {...sheetExtraProps}

    >

      <form id="client-add-form" className={aa.clientAddSheetForm} onSubmit={submit}>

        {formError ? (
          <p className={aa.clientAddSheetError} role="alert">
            {formError}
          </p>
        ) : null}

        <button

          type="button"

          className={`${aa.clientAddSheetParentToggle} ${

            isFamily ? aa.clientAddSheetParentToggleOn : ""

          }`}

          onClick={() => setIsFamily((v) => !v)}

          aria-pressed={isFamily}

        >

          <span className={aa.clientAddSheetParentToggleIcon} aria-hidden>

            👨‍👩‍👧

          </span>

          <span className={aa.clientAddSheetParentToggleText}>

            <span className={aa.clientAddSheetParentToggleTitle}>

              {isFamily ? "Семейный аккаунт" : "Добавить семью"}

            </span>

            <span className={aa.clientAddSheetParentToggleHint}>

              {isFamily

                ? "Родитель и один или несколько детей"

                : "Если клиент — ребёнок с родителем, включите семейный режим"}

            </span>

          </span>

          <span className={aa.clientAddSheetParentToggleChevron} aria-hidden>

            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">

              <path

                d="M9 6l6 6-6 6"

                stroke="currentColor"

                strokeWidth="2"

                strokeLinecap="round"

                strokeLinejoin="round"

              />

            </svg>

          </span>

        </button>



        {isFamily ? (

          <>

            <HubFormCard title="Родитель">

              <Field label="ФИО родителя">

                <Input

                  required

                  autoFocus

                  value={form.parentName}

                  onChange={(e) => setForm({ ...form, parentName: e.target.value })}

                  placeholder="Мария Иванова"

                />

              </Field>

              <Field

                label="Телефон родителя"

                hint="Необязательно"

              >

                <PhoneInput
                  value={form.parentPhone}
                  onChange={(v) => setForm({ ...form, parentPhone: v })}
                />

              </Field>

              <Field

                label="Email родителя"

                hint="Для приглашения в личный кабинет и входа"

              >

                <Input

                  type="email"

                  value={form.parentEmail}

                  onChange={(e) => setForm({ ...form, parentEmail: e.target.value })}

                  placeholder="parent@example.com"

                />

              </Field>

            </HubFormCard>



            <HubFormCard title="Дети">

              {children.map((child, idx) => (

                <div key={idx}>

                  <Field label={idx === 0 ? "ФИО ребёнка" : `ФИО ребёнка ${idx + 1}`}>

                    <Input

                      required={idx === 0}

                      value={child.fullName}

                      onChange={(e) => patchChild(idx, { fullName: e.target.value })}

                      placeholder="Иван Иванов"

                    />

                  </Field>

                  <ChildDobField

                    label="Дата рождения"

                    required={idx === 0}

                    value={child.date_of_birth}

                    onChange={(e) => patchChild(idx, { date_of_birth: e.target.value })}

                  />

                  <Field

                    label="Email ребёнка"

                    hint="Необязательно — для отдельного входа ребёнка в кабинет (приглашение на этот email)"

                  >

                    <Input

                      type="email"

                      value={child.email || ""}

                      onChange={(e) => patchChild(idx, { email: e.target.value })}

                      placeholder="child@example.com"

                    />

                  </Field>

                </div>

              ))}

              <Button

                type="button"

                size="sm"

                variant="secondary"

                onClick={() => setChildren((list) => [...list, { ...EMPTY_CHILD }])}

              >

                + Ещё ребёнок

              </Button>

              <Field label={L.group}>

                <Select

                  value={form.group_id}

                  onChange={(e) => setForm({ ...form, group_id: e.target.value })}

                >

                  <option value="">Без группы</option>

                  {groups.map((g) => (

                    <option key={g.id} value={g.id}>

                      {g.name}

                    </option>

                  ))}

                </Select>

              </Field>

            </HubFormCard>



            <HubFormCard title="Приглашение на портал">{inviteOptions}</HubFormCard>

          </>

        ) : (

          <>

            <HubFormCard title="Клиент">

              <Field label="ФИО">

                <Input

                  required

                  autoFocus

                  value={form.fullName}

                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}

                  placeholder="Иван Иванов"

                />

              </Field>

              <Field label="Телефон" hint="Необязательно">

                <PhoneInput
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />

              </Field>

              <Field

                label="Email"

                hint="Для приглашения в личный кабинет и входа"

              >

                <Input

                  type="email"

                  value={form.email}

                  onChange={(e) => setForm({ ...form, email: e.target.value })}

                  placeholder="client@example.com"

                />

              </Field>

              <ChildDobField

                label="Дата рождения"

                value={form.date_of_birth}

                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}

              />

              <Field label={L.group}>

                <Select

                  value={form.group_id}

                  onChange={(e) => setForm({ ...form, group_id: e.target.value })}

                >

                  <option value="">Без группы</option>

                  {groups.map((g) => (

                    <option key={g.id} value={g.id}>

                      {g.name}

                    </option>

                  ))}

                </Select>

              </Field>

            </HubFormCard>



            <HubFormCard title="Приглашение на портал">{inviteOptions}</HubFormCard>

          </>

        )}

      </form>

    </Sheet>

  );

}


